import type { RegistrationErrorBody, RegistrationErrorCode } from '@/types/registration';
import translator from '@/locale/translator';

/** The response body of a rejected request. Accepts any error shape, since the dev mock throws a plain object. */
export function readRegistrationErrorBody(error: unknown): Partial<RegistrationErrorBody> | undefined {
  const body = (error as { responseJson?: unknown } | null)?.responseJson;

  return body !== null && typeof body === 'object' ? (body as Partial<RegistrationErrorBody>) : undefined;
}

/** Reads the backend's error code. Branch on it, not the message: several outcomes share one status. */
export function readRegistrationErrorCode(error: unknown): RegistrationErrorCode | undefined {
  return readRegistrationErrorBody(error)?.code;
}

/** The backend's own wording, already localised for the request; undefined when the response carried none. */
export function readRegistrationErrorMessage(error: unknown): string | undefined {
  const message = readRegistrationErrorBody(error)?.message;

  return typeof message === 'string' && message.trim() !== '' ? message : undefined;
}

/**
 * The text to show for a rejected request: the backend's localised message when it sent one, otherwise the
 * fallback, which is either a fixed string or a choice made from the error code.
 */
export function registrationErrorMessage(
  error: unknown,
  fallback: string | ((code: RegistrationErrorCode | undefined) => string)
): string {
  const backendMessage = readRegistrationErrorMessage(error);
  if (backendMessage) return backendMessage;

  return typeof fallback === 'function' ? fallback(readRegistrationErrorCode(error)) : fallback;
}

/** Which step is showing the error; the wording of a wrong code and of an unknown failure differs between them. */
export type VerificationChannel = 'email' | 'mobile';

/** The frontend's own text for a verification error code, used when the backend sent no message. */
export function verificationErrorMessage(
  code: RegistrationErrorCode | undefined,
  channel: VerificationChannel
): string {
  switch (code) {
    case 'verification_expired':
      return translator.get('registration.verify.error_expired');
    case 'verification_too_many_attempts':
      return translator.get('registration.verify.error_too_many');
    case 'verification_throttled':
      return translator.get('registration.verify.error_throttled');
    case 'verification_delivery_unavailable':
      return translator.get('registration.profile.mobile_error_delivery_unavailable');
    case 'verification_invalid_phone':
      return translator.get('registration.profile.mobile_error_invalid_phone');
    case 'verification_already_verified':
      return translator.get('account.profile.phone.already_verified');
    case 'verification_code_incorrect':
      return translator.get(
        channel === 'email' ? 'registration.verify.error_incorrect' : 'registration.profile.mobile_error_incorrect'
      );
    default:
      return translator.get(
        channel === 'email' ? 'registration.verify.error_incorrect' : 'registration.profile.mobile_error_generic'
      );
  }
}
